import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { request } from 'app/network';
import { ApiParams } from 'app/network/types/ApiTypes';

/**
 * Standardized mutation hook for MediInStep.
 * Wraps React Query's useMutation with automatic invalidation support.
 *
 * @param apiName - The API endpoint name (e.g. "User.create")
 * @param options - React Query mutation options (optional)
 */
export function useApiMutation<TData = any, TVariables = ApiParams>(
    apiName: string,
    options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>
) {
    const queryClient = useQueryClient();

    return useMutation<TData, Error, TVariables>({
        mutationFn: async (variables) => {
            const response = await request(apiName, { paramz: variables as unknown as ApiParams });
            return response.data;
        },
        onSuccess: (data, variables, context) => {
            // Automatic invalidation strategy can be enhanced here.
            // For now, we rely on the component to specify invalidations or invalidate the Group if needed.
            // Example: If apiName is "User.create", we might want to invalidate queries starting with "User".
            const groupName = apiName.split('.')[0];
            if (groupName) {
                queryClient.invalidateQueries({ queryKey: [groupName] });
            }

            // @ts-ignore
            options?.onSuccess?.(data, variables, context);
        },
        ...options,
    });
}
